/**
 * Boundary translation between the marketplace UI's flat
 * `PropertySearchQuery` filter shape and Athena's product-shaped
 * `SavedSearchInput` / `SavedSearchFieldsFragment`.
 *
 * These functions are pure: no React, no I/O. The hook and UI components
 * compose them with `useSearchState` to round-trip filters.
 */
import { SavedSearchPropertyType } from '__generated__/athena/graphql';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena/graphql';

import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import type { GeoPoint } from '../map/types';

export const SUPPORTED_PROPERTY_TYPES: ReadonlyArray<SavedSearchPropertyType> = [
  SavedSearchPropertyType.HOME,
  SavedSearchPropertyType.CONDO,
  SavedSearchPropertyType.TOWNHOME,
];

const buildPropertyTypes = (query: PropertySearchQuery): SavedSearchPropertyType[] => {
  const types: SavedSearchPropertyType[] = [];
  if (query.singleFamily) {
    types.push(SavedSearchPropertyType.HOME);
  }
  if (query.condo) {
    types.push(SavedSearchPropertyType.CONDO);
  }
  if (query.townhome) {
    types.push(SavedSearchPropertyType.TOWNHOME);
  }
  // Collapse "all selected" to "any" so the persisted search behaves like
  // the UI default state (matches `getInitialPropertyTypes` in useSearchState.ts).
  return types.length === SUPPORTED_PROPERTY_TYPES.length ? [] : types;
};

// The Cosmos UI exposes a strict subset of `SavedSearchFilters` (no upper
// bounds for beds/baths, no Opendoor-owned or FHA toggles, and HOA is a
// single `noHoas` boolean rather than a numeric cap). Without preserving the
// caller-provided previous record, an update from Cosmos would silently
// clobber whatever another client (mobile, athlete-fe) stored in those
// fields. The `previous` parameter is the existing `SavedSearchFieldsFragment`
// being edited; pass `null`/omit on the initial add path.
const resolveHoaCap = (
  noHoas: boolean | null | undefined,
  previousHoaCap: number | null | undefined,
): number | null => {
  if (noHoas) {
    return 0;
  }
  // `noHoas: false` in Cosmos can mean either "no HOA filter" or "I never
  // saw an HOA filter to toggle off" — both render identically in the UI.
  // If the previous record had a finite non-zero cap (e.g. $500), preserve
  // it so editing an unrelated field doesn't wipe out an HOA cap set on
  // another surface.
  if (previousHoaCap != null && previousHoaCap > 0) {
    return previousHoaCap;
  }
  return null;
};

export function toSavedSearchInput(
  query: PropertySearchQuery,
  name: string,
  previous?: SavedSearchFieldsFragment | null,
): SavedSearchInput {
  const previousFilters = previous?.filters;
  return {
    name,
    location: {
      market: query.market ?? null,
      zoneIds: query.zoneIds ?? null,
      vertices: query.vertices ?? null,
    },
    filters: {
      minPriceDollars: query.minListPrice ?? null,
      maxPriceDollars: query.maxListPrice ?? null,
      maxMonthlyHoaFeeDollars: resolveHoaCap(
        query.noHoas,
        previousFilters?.maxMonthlyHoaFeeDollars,
      ),
      minBedrooms: query.bedrooms ?? null,
      // Cosmos has no UI for max-bound bed/bath filters; preserve any value
      // set by another client instead of clobbering it with `null` on update.
      maxBedrooms: previousFilters?.maxBedrooms ?? null,
      minBathrooms: query.bathrooms ?? null,
      maxBathrooms: previousFilters?.maxBathrooms ?? null,
      minSqft: query.minSquareFootage ?? null,
      maxSqft: query.maxSquareFootage ?? null,
      minLotSqft: query.minLotSize ?? null,
      maxLotSqft: query.maxLotSize ?? null,
      minYearBuilt: query.minYearBuilt ?? null,
      maxYearBuilt: query.maxYearBuilt ?? null,
      // Same rationale as max bed/bath: no Cosmos UI, so preserve on update.
      opendoorOwnedOnly: previousFilters?.opendoorOwnedOnly ?? false,
      selfTourableOnly: query.canSelfTour ?? false,
      singleStoryOnly: query.hasSingleStory ?? false,
      hasPoolOnly: query.hasPool ?? false,
      fhaEligibleOnly: previousFilters?.fhaEligibleOnly ?? false,
      propertyTypes: buildPropertyTypes(query),
    },
  };
}

export function toPropertySearchQuery(saved: SavedSearchFieldsFragment): PropertySearchQuery {
  const types = saved.filters.propertyTypes;
  const allOrNone = types.length === 0 || types.length === SUPPORTED_PROPERTY_TYPES.length;
  const vertices: GeoPoint[] | undefined =
    saved.location.vertices.length > 0
      ? saved.location.vertices.map((v) => ({ lat: v.lat, lon: v.lon }))
      : undefined;
  return {
    market: saved.location.market ?? undefined,
    zoneIds: saved.location.zoneIds.length > 0 ? [...saved.location.zoneIds] : undefined,
    vertices,
    bedrooms: saved.filters.minBedrooms ?? null,
    bathrooms: saved.filters.minBathrooms ?? null,
    minListPrice: saved.filters.minPriceDollars ?? null,
    maxListPrice: saved.filters.maxPriceDollars ?? null,
    minSquareFootage: saved.filters.minSqft ?? null,
    maxSquareFootage: saved.filters.maxSqft ?? null,
    minLotSize: saved.filters.minLotSqft ?? null,
    maxLotSize: saved.filters.maxLotSqft ?? null,
    minYearBuilt: saved.filters.minYearBuilt ?? null,
    maxYearBuilt: saved.filters.maxYearBuilt ?? null,
    noHoas: saved.filters.maxMonthlyHoaFeeDollars === 0,
    canSelfTour: saved.filters.selfTourableOnly || null,
    hasSingleStory: saved.filters.singleStoryOnly || null,
    hasPool: saved.filters.hasPoolOnly || null,
    singleFamily: allOrNone || types.includes(SavedSearchPropertyType.HOME),
    condo: allOrNone || types.includes(SavedSearchPropertyType.CONDO),
    townhome: allOrNone || types.includes(SavedSearchPropertyType.TOWNHOME),
  };
}

export function hasValidLocation(query: PropertySearchQuery): boolean {
  return !!query.market || (query.zoneIds?.length ?? 0) > 0 || (query.vertices?.length ?? 0) > 0;
}

/**
 * Builds the update input for a rename: round-trips the full record through
 * the mapping layer so unmapped fields (max bed/bath, opendoor-owned, FHA,
 * finite HOA cap) survive the update, with only the name changed.
 */
export function buildRenameInput(
  saved: SavedSearchFieldsFragment,
  newName: string,
): SavedSearchInput {
  return toSavedSearchInput(toPropertySearchQuery(saved), newName, saved);
}

// Canonical, order-insensitive projection of a SavedSearchInput used for
// equality checks (dirty state, duplicate detection). Name is deliberately
// excluded: two searches are "the same search" when location + filters match.
//
// Location is projected through search precedence (vertices > zoneIds >
// market): lower-precedence fields are ignored by the search, so they must be
// ignored by equality too. E.g. a zone search saved from a market page carries
// `market` while the same search saved from the zone route does not — both run
// the identical search and must compare equal.
const normalizeSavedSearchInput = (input: SavedSearchInput) => {
  const vertices = (input.location.vertices ?? []).map((v) => [v.lat, v.lon]);
  const zoneIds = vertices.length > 0 ? [] : [...(input.location.zoneIds ?? [])].sort();
  const market = vertices.length > 0 || zoneIds.length > 0 ? null : (input.location.market ?? null);
  return {
    location: { market, zoneIds, vertices },
    filters: {
      ...input.filters,
      propertyTypes: [...(input.filters.propertyTypes ?? [])].sort(),
    },
  };
};

const isSameSavedSearchInput = (a: SavedSearchInput, b: SavedSearchInput): boolean =>
  JSON.stringify(normalizeSavedSearchInput(a)) === JSON.stringify(normalizeSavedSearchInput(b));

/**
 * Whether `query` still matches the saved search it was applied from.
 *
 * The page side is projected without a preservation source — that is what
 * Cosmos actually searched with. The saved side keeps the full record
 * (including fields Cosmos cannot express). Re-injecting preserved fields on
 * both sides would report a clean match even when results are broader than
 * the saved search (e.g. maxBedrooms / FHA / finite HOA cap dropped on apply).
 */
export function queryMatchesSavedSearch(
  query: PropertySearchQuery,
  saved: SavedSearchFieldsFragment,
): boolean {
  return isSameSavedSearchInput(
    toSavedSearchInput(query, saved.name),
    buildRenameInput(saved, saved.name),
  );
}

/**
 * Finds an existing saved search equivalent to `query` (same location +
 * filters, any name), for duplicate detection at save time.
 */
export function findMatchingSavedSearch(
  query: PropertySearchQuery,
  savedSearches: SavedSearchFieldsFragment[],
): SavedSearchFieldsFragment | undefined {
  return savedSearches.find((saved) => queryMatchesSavedSearch(query, saved));
}

export interface SaveSearchQuerySources {
  /** Canonical, location-free filter state from `useSearchState().getCanonical()`. */
  canonical: PropertySearchQuery;
  /**
   * Canonical market routing slug (`MarketInfo.identifier`) — never the display
   * `name`. On zone-based pages `identifier` is null while `name` is a
   * human-readable label (e.g. "Downtown Phoenix"); persisting that label into
   * `location.market` would later be routed verbatim to `/homes/<label>`, which
   * fails SSR market resolution. Pass the identifier (or null) so zoneIds /
   * vertices remain the location source when there is no canonical slug.
   */
  marketIdentifier: string | null | undefined;
  selectedZoneId?: string | null;
  routeZoneIds?: string[] | null;
  /**
   * A user-drawn polygon only — never the market's own boundary shape. The
   * controller holds "no drawn polygon" as null, so an unpanned market page
   * saves a clean market-level record instead of a "custom area" polygon.
   */
  vertices: GeoPoint[] | null | undefined;
}

export function buildSaveSearchQuery({
  canonical,
  marketIdentifier,
  selectedZoneId,
  routeZoneIds,
  vertices,
}: SaveSearchQuerySources): PropertySearchQuery {
  let zoneIds: string[] | null = null;
  if (selectedZoneId) {
    zoneIds = [selectedZoneId];
  } else if (routeZoneIds?.length) {
    zoneIds = routeZoneIds;
  }

  const hasDrawnPolygon = !!vertices && vertices.length > 0;
  return {
    ...canonical,
    market: marketIdentifier ?? null,
    zoneIds: hasDrawnPolygon ? null : zoneIds,
    vertices: hasDrawnPolygon ? vertices : null,
  };
}
